import { useState, useMemo, useCallback, useEffect } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { debounce } from 'lodash'
import { TypographyStyles, TextColors } from '@/styles'
import { Textarea, Text, Link } from '@/ui'
import { TransactionOrSplit } from '@/reducers/admin/allTransactions.slice'
import { updateUserTransaction } from '../transactions.slice'
import { useAnalyticsTrack } from '@/features/amplitude'

export const NotesInput = ({
  transaction,
}: {
  transaction: TransactionOrSplit
}) => {
  const { notes, confirmedAt } = transaction

  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()

  const [transactionNote, setTransactionNote] = useState<string | undefined>(
    notes || ''
  )

  const debouncedUpdateNotes = useMemo(
    () =>
      debounce((newNotes: string | undefined) => {
        if ((transaction.notes || '') === (newNotes || '')) return
        track('updated notes', {
          note_text: newNotes ?? '',
          transaction_status: confirmedAt ? 'post-confirm' : 'pre-confirm',
          transaction_id: transaction.id,
        })
        updateUserTransaction(transaction.id, {
          notes: newNotes,
          notesLastUpdatedAt: new Date().toISOString(),
        })(dispatch)
      }, 2000),
    [confirmedAt, transaction.notes, transaction.id, dispatch, track]
  )

  const handleChange = useCallback(
    (newValue: string | undefined) => {
      setTransactionNote(newValue)
      debouncedUpdateNotes(newValue)
    },
    [debouncedUpdateNotes]
  )

  useEffect(() => {
    // Cleanup function to update notes when the component unmounts
    return () => {
      debouncedUpdateNotes.flush() // Execute any pending debounced updates immediately
    }
  }, [debouncedUpdateNotes])

  const noteDisclaimer = confirmedAt
    ? 'The accounting team has already reviewed this transaction. If you have questions, please reach out via'
    : 'If you have questions or an auto-categorization request, please reach out via'

  return (
    <>
      <Text
        variant={TypographyStyles.LABEL_MD}
        className="mb-2"
        color={TextColors.MUTED}
      >
        Notes
      </Text>
      <Textarea
        rows={2}
        placeholder="Add details about this transaction"
        value={transactionNote || ''}
        onChange={(e) => handleChange(e.target.value)}
        className="w-full text-md"
      />
      <Text
        variant={TypographyStyles.BODY_SM}
        color={TextColors.MUTED}
        className="mt-4"
      >
        {noteDisclaimer}{' '}
        <Link
          href={`/conversations?newConversation=true&transactionId=${transaction.id}`}
          trackMessage="clicked submit transaction question"
          newPage={false}
          trackAttributes={{
            transaction_status: confirmedAt ? 'post-confirm' : 'pre-confirm',
          }}
        >
          Messages
        </Link>
        .
      </Text>
    </>
  )
}
